<script setup lang="ts">
import {useGameStore} from "@/stores/gameStore.ts";
import { computed, ref, watch } from 'vue'
import { useAssetStore } from '@/stores/assetStore.ts'
import { usePlanetStore } from '@/stores/planetStore.ts'
import { CDN } from '@/models/cdn.ts'
import MetricDisplay from '@/components/Elements/Generic/MetricDisplay.vue'
import LargeModel from "../ModelSizes/LargeModel.vue";
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import Backend from '@/models/backend'
import RangeSlider from '@/components/Elements/Forms/RangeSlider.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import { logger } from '@/models/logger'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Get the planet data
const gameStore = useGameStore()
const assetStore = useAssetStore()
const planetStore = usePlanetStore()
const backend = new Backend()

const outputOre = computed(() => {
  return assetStore.findOreById(gameStore.modalData.outputOreId)
})

const leftOre = computed(() => {
  return assetStore.findOreById(gameStore.modalData.inputOreLeftId)
})

const rightOre = computed(() => {
  return assetStore.findOreById(gameStore.modalData.inputOreRightId)
})

const factoryCount = computed(() => {
  return planetStore.countBuildingsOfType(gameStore.modalData.buildingType)
})

// Active limit slider
const activeLimit = ref(0)

// Initialize and sync activeLimit from gameStore.playerBuildingSettings
watch(() => [gameStore.modalData.buildingType, gameStore.playerBuildingSettings], ([buildingType]) => {
  if (buildingType) {
    activeLimit.value = gameStore.playerBuildingSettings.get(buildingType as string) || 0
  }
}, { immediate: true, deep: true })

// Update active limit
const updateActiveLimit = () => {
  backend.setBuildingActiveLimit(gameStore.modalData.buildingType, activeLimit.value)
    .then(() => {
      gameStore.setTinySuccessToastMessage(t('modals.oreRefinery.messages.activeLimitUpdated'))
    })
    .catch((error) => {
      logger.error('Failed to update active limit:', error)
      gameStore.setTinyErrorToastMessage(t('modals.oreRefinery.messages.activeLimitFailed'))
    })
}

</script>

<template>
  <LargeModel :title="t('modals.oreRefinery.title', { ore: outputOre.oreName })">
    <div class="flex flex-col sm:flex-row text-center sm:text-left p-2">
      <video
        :src="CDN.buildingVideo(512,gameStore.modalData.buildingType)"
        autoplay
        loop
        muted
        playsinline
        class="self-center sm:self-start rounded w-auto md:w-1/2"
      ></video>
      <div class="text-subtext ps-0 md:ps-2 pt-3 md:pt-0 w-full flex flex-col justify-top">
        <StandardCard top-padding="0">
          <div>{{ t('modals.oreRefinery.convertsPart1') }} {{ outputOre.dependency1ConversionRate }} <GenericIcon :type="'ore' + leftOre.id"/> {{ leftOre.oreName }} {{ t('modals.oreRefinery.convertsPart2') }} {{ outputOre.dependency2ConversionRate }} <GenericIcon :type="'ore' + rightOre.id"/> {{ rightOre.oreName }} {{ t('modals.oreRefinery.convertsPart3') }} <GenericIcon :type="'ore' + outputOre.id"/> {{ outputOre.oreName }}</div>
        </StandardCard>
        <StandardCard>
          <p>{{ t('modals.oreRefinery.limitDescription') }}</p>
          <p class="pt-2">{{ t('modals.oreRefinery.setMaxDescription') }}</p>
          <div class="pt-4">
            <div class="flex items-center gap-3">
              <span class="text-sm text-metal-300 whitespace-nowrap">{{ t('modals.oreRefinery.maxActive') }}</span>
              <RangeSlider
                v-model="activeLimit"
                :min="0"
                :max="100"
                :step="1"
                class="flex-1"
                @update:modelValue="updateActiveLimit"
              />
              <span class="text-sm text-metal-300 min-w-[3rem] text-right">{{ activeLimit }}</span>
            </div>
          </div>
        </StandardCard>

        <div class="pt-1 flex justify-between">
          <MetricDisplay :text="t('modals.oreRefinery.factories')">{{ factoryCount }}</MetricDisplay>
          <MetricDisplay :text="t('modals.oreRefinery.processedLastWeek')">{{ gameStore.modalData.capacity }}</MetricDisplay>
        </div>
      </div>
      </div>
    </LargeModel>
</template>


<style scoped lang="scss">
.slider::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: 2px solid #1e293b;
}

.slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: 2px solid #1e293b;
}

.slider:focus {
  outline: none;
}

.slider:focus::-webkit-slider-thumb {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}
</style>